import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PayerType, Therapy } from '@pia/pia.shared';
import { cloneDeep } from 'lodash';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { getSelectionItems } from 'src/app/business/selectionitem-factory';
import { IPatientListFilter } from 'src/app/common/contracts/filter/patient-list-filter';
import { PatientListFilter } from 'src/app/common/filter/patient-list-filter';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { Therapies } from 'src/app/shared/models/therapy';
import { InstitutionDataService } from 'src/app/shared/services/institution-data.service';

export enum LastVisitChoice {
  anyTime,
  sixMonth,
  twelveMonth,
  none,
}

@Component({
  selector: 'app-patient-list-filter',
  templateUrl: './patient-list-filter.component.html',
  styleUrls: ['./patient-list-filter.component.scss'],
})
export class PatientListFilterComponent implements OnInit, OnDestroy {
  @Input() patientListFilter: IPatientListFilter;
  @Input() isMyPatientList = false;

  public form: UntypedFormGroup;
  public subunitSelectionItems: SelectionItem[] = [];
  private _ngUnsubscribe = new Subject();
  public lastVisitValue: LastVisitChoice = LastVisitChoice.anyTime;
  public lastVisitTypes: SelectionItem[] = [];
  public therapySelectionItems = Therapies.therapyItems.map(t => new SelectionItem(t.id, t.displayName));
  public therapyTypeSelectionItems: SelectionItem[] = [];
  public payerTypes: SelectionItem[] = [];
  public payerRestriction: any;
  public PayerType = PayerType;

  constructor(
    private _modalController: ModalController,
    private _formBuilder: UntypedFormBuilder,
    private _institutionDataService: InstitutionDataService,
    private _translate: TranslateService
  ) {
    this.form = this._formBuilder.group({
      fieldNurseId: [null],
      nursingHomeId: [null],
      subunitId: [null],
      nursingServiceId: [null],
      showDisabledPatients: [null],
      lastVisit: [null],
      therapyId: [null],
      therapyTypeId: [null],
      payerType: [null],
      payerId: [null],
    });
    this.therapySelectionItems.remove(this.therapySelectionItems.find(item => item.id === Therapy.H_UNBEK));
    this.lastVisitTypes.push(new SelectionItem(LastVisitChoice.anyTime, 'Kein Filter'));
    this.lastVisitTypes.push(new SelectionItem(LastVisitChoice.sixMonth, 'Vor mehr als 6 Monaten'));
    this.lastVisitTypes.push(new SelectionItem(LastVisitChoice.twelveMonth, 'Vor mehr als 12 Monaten'));
    this.lastVisitTypes.push(new SelectionItem(LastVisitChoice.none, 'Noch nie'));
    this.payerTypes = getSelectionItems(PayerType, 'patient.edit.payer_types.', this._translate).filter(
      payerType =>
        payerType.id !== PayerType.Self &&
        payerType.id !== PayerType.Unknown &&
        payerType.id !== PayerType.Other &&
        payerType.id !== PayerType.TradeAssociation
    );
  }

  async ngOnInit(): Promise<void> {
    const patientListFilter = cloneDeep(this.patientListFilter);
    const { filterArgs } = patientListFilter;
    this.form.patchValue({
      fieldNurseId: filterArgs ? filterArgs.fieldNurseId : null,
      nursingHomeId: filterArgs ? filterArgs.nursingHomeId : null,
      subunitId: filterArgs ? filterArgs.subunitId : null,
      nursingServiceId: filterArgs ? filterArgs.nursingServiceId : null,
      lastVisit: filterArgs ? patientListFilter.lastVisit : 0,
      therapyId: patientListFilter.therapyId,
      therapyTypeId: patientListFilter.therapyTypeId,
      showDisabledPatients: patientListFilter.showDisabledPatients,
      payerType: patientListFilter.payerType,
      payerId: patientListFilter.payerId,
    });
    this.lastVisitValue = patientListFilter.lastVisit;
    if (filterArgs.subunitId) {
      await this.loadAndSetSubUnits();
    }
    this.form.controls['nursingHomeId'].valueChanges
      .pipe(takeUntil(this._ngUnsubscribe), distinctUntilChanged())
      .subscribe(async () => {
        this.form.controls['subunitId'].setValue(undefined);
        await this.loadAndSetSubUnits();
      });
    if (patientListFilter.therapyId != null) {
      const therapy = Therapies.getTherapy(patientListFilter.therapyId);

      this.therapyTypeSelectionItems =
        therapy?.therapyTypes?.map(type => new SelectionItem(type.id, type.displayName)) ?? [];
    }
    this.form.controls['therapyId'].valueChanges
      .pipe(takeUntil(this._ngUnsubscribe), distinctUntilChanged())
      .subscribe(id => {
        this.form.controls['therapyTypeId'].setValue(null);
        const therapy = Therapies.getTherapy(id);
        this.therapyTypeSelectionItems =
          therapy?.therapyTypes?.map(type => new SelectionItem(type.id, type.displayName)) ?? [];
      });
    this.payerRestriction = {
      type: this.form.controls['payerType'].value,
    };
    this.form.controls['payerType'].valueChanges
      .pipe(takeUntil(this._ngUnsubscribe), distinctUntilChanged())
      .subscribe(value => {
        this.form.controls['payerId'].setValue(null);

        this.payerRestriction = {
          type: value,
        };
      });
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next(true);
    this._ngUnsubscribe.complete();
  }

  public async cancel(): Promise<void> {
    await this._modalController.dismiss(this.patientListFilter);
  }

  public async done(): Promise<void> {
    if (this.form.dirty === true) {
      const { showDisabledPatients, lastVisit, therapyId, therapyTypeId, payerType, payerId, ...filterArgs } =
        this.form.value;
      await this._modalController.dismiss(
        new PatientListFilter(filterArgs, lastVisit, showDisabledPatients, therapyId, therapyTypeId, payerType, payerId)
      );
    }
  }

  public async resetFilter() {
    await this._modalController.dismiss(new PatientListFilter({}, 0, false));
  }

  private async loadAndSetSubUnits(): Promise<void> {
    await this._institutionDataService
      .getSubunitSelectionItems(this.form.controls['nursingHomeId'].value)
      .then(subunits => {
        if (subunits) {
          this.subunitSelectionItems = subunits;
        } else {
          this.subunitSelectionItems = [];
        }
      });
  }
}
